<template>
  <section
    :id="`${$options.name}-${instanceId}`"
    ref="preCheckIn"
    class="pre-checkin"
    data-module="pre-checkin"
  >
    <div
      v-if="(loadingIntegration || loading) && !preview"
      class="validate-integration"
    >
      <Loader />
      <p v-if="loadingIntegration">
        Estamos buscando os dados da sua reserva! ✈
      </p>
    </div>
    <SignInForm
      v-else-if="activeGuestIndex !== null && !preview"
      :key="isFirstView"
      :active-guest="guests[activeGuestIndex]"
      :active-guest-index="activeGuestIndex"
      :main-guest="mainGuest"
      :is-first-view="isFirstView"
      :has-integration="hasIntegration"
      :has-pms-integration="hasPmsIntegration"
      :settings="integrationSettings"
      :consent-text="consentText"
      @cancel="cancel"
      @save="saveGuest"
    />
    <Board
      v-else-if="!isDone || preview === 'board'"
      :title="titleBoard"
      :subtitle="subtitleBoard"
      :guests="guests"
      :main-guest="mainGuest"
      :is-sending="isSending"
      :error="error"
      :has-integration="hasIntegration"
      @edit="editGuest"
      @editReservation="editReservation"
      @add="addGuest"
      @end="send"
      @checkSendCopyToGuest="checkSendCopyToGuest"
    />
    <Success
      v-if="(activeGuestIndex === null && isDone) || preview === 'success'"
      :image="imageSuccess"
      :alt-image="altImageSuccess"
      :title="titleSuccess"
      :subtitle="subtitleSuccess"
      :steps="stepsSuccess"
    />
  </section>
</template>

<script>
import Vue from 'vue';
import { mapState, mapGetters } from 'vuex';

import Loader from '@/components/Loader.vue';
import getApiHotels from '@/mixins/getApiHotels';
import SignInForm from './SignInForm.vue';
import Board from './Board.vue';
import Success from './Success.vue';
import { getData, submitData, submitEmail } from './handlers';

export default {
  name: 'PreCheckIn',
  components: {
    SignInForm,
    Board,
    Success,
    Loader,
  },
  mixins: [getApiHotels],
  inheritAttrs: false,
  props: {
    instanceId: {
      type: String,
      default: '',
    },
    titleBoard: {
      type: String,
      default: '',
    },
    subtitleBoard: {
      type: String,
      default: '',
    },
    emails: {
      type: Array,
      default: () => [],
    },
    hotel: {
      type: String,
      default: '',
    },
    travelHubHotelCode: {
      type: String,
      default: null,
    },
    travelHubToken: {
      type: String,
      default: null,
    },
    consentText: {
      type: String,
      default: '',
    },
    imageSuccess: {
      type: String,
      default: '',
    },
    altImageSuccess: {
      type: String,
      default: '',
    },
    titleSuccess: {
      type: String,
      default: '',
    },
    subtitleSuccess: {
      type: String,
      default: '',
    },
    stepsSuccess: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      guests: [
        {
          countryCode: 'BR',
          nationality: 'BR',
          stateCode: '',
          stateName: '',
          profession: '',
          isMainGuest: true,
          contactOptin: false,
          consentText: this.consentText,
        },
      ],
      integrationEmails: [],
      activeGuestIndex: 0,
      backupGuest: null,
      integrationSettings: { origin: '' },
      integrationGetError: false,
      isDone: false,
      hotels: [],
      isFirstView: true,
      isAdding: false,
      isSending: false,
      sendCopyToGuest: false,
      error: false,
      loadingIntegration: false,
      loading: true,
      preview: '',
    };
  },
  computed: {
    hasIntegration() {
      return !!this.travelHubToken && !this.integrationGetError;
    },
    hasPmsIntegration() {
      return this.hasIntegration && !!this.travelHubHotelCode;
    },
    mainGuest() {
      return (
        this.guests.find(({ isMainGuest }) => isMainGuest) || this.guests[0]
      );
    },
    integrationParameters() {
      if (this.hasIntegration && this.isFirstView) {
        const { Surname, ReservationCode, Checkin } = this.$route.query;

        const isSurnameValid =
          Surname && /^[\u00c0-\u017fa-zA-Z .'-]+$/.test(Surname);

        const isReservationCodeValid =
          ReservationCode && /^\w+$/.test(ReservationCode);

        const isCheckinValid =
          Checkin &&
          /^([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))$/.test(Checkin);

        if (isSurnameValid && isReservationCodeValid && isCheckinValid) {
          return { Surname, ReservationCode, Checkin };
        }
      }
      return null;
    },
    ...mapState(['activeInstance', 'activeInstanceExpandedGroup']),
    ...mapGetters(['settings', 'hasLetsbookIntegration']),
  },
  watch: {
    activeGuestIndex() {
      this.scrollUp();
    },
    isDone() {
      this.scrollUp();
    },
    activeInstanceExpandedGroup(openGroup) {
      if (this.instanceId === this.activeInstance) {
        if (openGroup === '0-1') {
          this.preview = 'board';
        } else if (openGroup === '0-2') {
          this.preview = 'success';
          this.isDone = true;
        } else {
          this.preview = '';
          this.isDone = false;
        }
      } else {
        this.preview = '';
        this.isDone = false;
      }
    },
  },
  created() {
    if (this.integrationParameters) {
      this.loadingIntegration = true;
    }
  },
  mounted() {
    this.loading = false;

    if (this.hasLetsbookIntegration) {
      this.getApiHotels();
    }

    const precheckinStorage = localStorage.getItem('editor-precheckin');

    if (precheckinStorage) {
      const { guests, expires, hasIntegration, integrationSettings } =
        JSON.parse(precheckinStorage);
      const now = new Date().getTime();

      if (
        (expires && expires <= now) ||
        new Date(guests[0].checkInDate.split('/').reverse().join('/')) < now
      ) {
        localStorage.removeItem('editor-precheckin');
      } else {
        this.integrationGetError = !hasIntegration;
        this.activeGuestIndex = null;
        this.isFirstView = false;
        this.guests = guests;
        this.integrationSettings = integrationSettings;
      }
    }
  },
  methods: {
    cancel() {
      if (!this.isFirstView && !this.isAdding && this.backupGuest) {
        // cancelando a edição
        Vue.set(this.guests, this.activeGuestIndex, this.backupGuest);
        this.backupGuest = null;
      }

      if (this.isAdding) {
        // cancelando a criação de um novo acompanhante
        this.guests.splice(this.activeGuestIndex, 1);
        this.isAdding = false;
      }

      this.unsetGuest();
    },
    unsetGuest() {
      if (this.isFirstView) {
        this.isFirstView = false;
      }

      this.activeGuestIndex = null;
    },
    saveGuest(guest) {
      if (this.isFirstView && this.hasIntegration) {
        this.loadingIntegration = true;

        getData(guest, {
          travelHubToken: this.travelHubToken,
          travelHubHotelCode: this.travelHubHotelCode,
        })
          .then(({ guests, ...integrationSettings }) => {
            guests.sort((gst) => (gst.isMainGuest ? -1 : 0)); // move mainGuest para primeira posição

            const integrationGuests = [...guests];
            const mainGuestIndex = 0;

            integrationGuests[mainGuestIndex] = {
              ...guest,
              ...guests[mainGuestIndex],
              isMainGuest: true,
            };
            this.guests = integrationGuests;
            this.activeGuestIndex = mainGuestIndex;
            this.integrationSettings = { ...integrationSettings };
            this.updateLocalStorageGuests(this.guests);
          })
          .catch((err) => {
            console.warn(err);
            this.integrationGetError = true;
            this.guests = [{ ...guest }];
            this.updateLocalStorageGuests(this.guests);
          })
          .finally(() => {
            this.loadingIntegration = false;
          });
      } else {
        this.guests[this.activeGuestIndex] = { ...guest, isValid: true };
        this.updateLocalStorageGuests(this.guests);
        this.unsetGuest();
        this.isAdding = false;
      }
      this.isFirstView = false;
    },
    updateLocalStorageGuests(guests) {
      this.$nextTick(() => {
        const storageItem = {
          guests,
          hasIntegration: this.hasIntegration,
          expires: new Date().getTime() + 1000 * 60 * 60 * 24 * 30, // Expires in 30 days
          integrationSettings: this.integrationSettings,
        };
        localStorage.setItem('editor-precheckin', JSON.stringify(storageItem));
      });
    },
    editGuest(index) {
      this.backupGuest = { ...this.guests[index] };
      this.activeGuestIndex = index;
    },
    editReservation() {
      this.isFirstView = true;
      this.activeGuestIndex = 0;

      if (this.travelHubToken) {
        this.integrationEmails = [];
        this.integrationSettings = { origin: '' };
        this.integrationGetError = false;
        this.guests = [
          {
            countryCode: 'BR',
            nationality: 'BR',
            stateCode: '',
            stateName: '',
            profession: '',
            isMainGuest: true,
            contactOptin: false,
            consentText: this.consentText,
          },
        ];
      }
    },
    addGuest() {
      this.guests.push({
        countryCode: 'BR',
        nationality: 'BR',
        stateCode: '',
        stateName: '',
        profession: '',
        isMainGuest: false,
        contactOptin: false,
        consentText: this.consentText,
      });
      this.isAdding = true;
      this.activeGuestIndex = this.guests.length - 1;
    },
    copyMainGuestAddress(guestIndex) {
      const address = {
        countryCode: this.mainGuest.countryCode,
        countryName: this.mainGuest.countryName,
        cep: this.mainGuest.cep,
        address: this.mainGuest.address,
        addressNumber: this.mainGuest.addressNumber,
        addressComplement: this.mainGuest.addressComplement,
        stateCode: this.mainGuest.stateCode,
        stateName: this.mainGuest.stateName,
        cityName: this.mainGuest.cityName,
        cityCode: this.mainGuest.cityCode,
      };

      this.guests[guestIndex] = { ...this.guests[guestIndex], ...address }; // update address
    },
    checkSendCopyToGuest(isChecked) {
      this.sendCopyToGuest = isChecked;
    },

    sendEmail() {
      const body = {
        hotel: this.hotel,
        emails: this.emails,
        integrationEmails: this.integrationEmails,
        guests: this.guests,
        sendCopyConfirmation: this.sendCopyToGuest,
        hasIntegration: this.hasIntegration,
      };
      const { apiBaseUrl, customer } = this.$store.state;
      submitEmail(body, apiBaseUrl, customer)
        .then((res) => {
          this.isDone = res.status === 200;
          if (this.isDone) {
            localStorage.removeItem('guests');
          } else {
            console.log('Ops! Algo errado aconteceu.');
            this.error = true;
            setTimeout(() => {
              this.error = false;
            }, 4000);
          }
        })
        .catch(() => {
          this.error = true;
          setTimeout(() => {
            this.error = false;
          }, 4000);
        })
        .finally(() => {
          this.isSending = false;
        });
    },
    submitIntegration() {
      submitData(this.guests, {
        ...this.integrationSettings,
        travelHubToken: this.travelHubToken,
        travelHubHotelCode: this.travelHubHotelCode,
      })
        .then((res) => res.json())
        .then(({ data = '' }) => {
          if (data && this.integrationSettings.origin === 'Letsbook') {
            this.integrationEmails = [...data.split(';')];
          }
        })
        .catch((reason) => {
          console.warn(reason);
        })
        .finally(() => {
          this.sendEmail();
        });
    },
    send() {
      this.isSending = true;

      if (this.guests.length > 1) {
        this.guests.forEach((guest, index) => {
          if (guest.useMainGuestAddress) {
            this.copyMainGuestAddress(index);
          }
        });
      }

      if (this.hasIntegration) {
        this.submitIntegration();
      } else {
        this.sendEmail();
      }
    },
    scrollUp() {
      const scroll = document.querySelector('.preview > .scroll-area');
      const { offsetTop } = this.$refs.preCheckIn;
      (scroll || window).scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    },
  },
  metadata: {
    tabs: [
      {
        name: 'content',
        label: 'Conteúdo',
        groups: [
          {
            name: 'config',
            label: 'Configurações',
            fields: [
              {
                name: 'hotel',
                type: 'VueSelect',
                label: 'Selecione o hotel em que o hóspede fará o pré check-in',
                options() {
                  const hotelsToOptions = (hotels) =>
                    hotels.map((hotel) => ({
                      label: hotel.Nome,
                      value: hotel.Nome,
                    }));

                  if (this.hotels.length > 0) {
                    return hotelsToOptions(this.hotels);
                  }

                  return new Promise((resolve) => {
                    const unwatch = this.$watch('hotels', (hotels) => {
                      resolve(hotelsToOptions(hotels));
                      unwatch();
                    });
                  });
                },
              },
              {
                name: 'emails',
                type: 'Tags',
                label: 'Destinatários',
                whitelistFromCustomerSetting: 'emails',
                validation: {
                  presence: {
                    allowEmpty: false,
                    message:
                      'Adicione, no mínimo, um destinatário para o recebimento dos e-mails',
                  },
                },
              },
              {
                name: 'consentText',
                type: 'Html',
                maxlength: 200,
                label: 'Texto de consentimento',
              },
            ],
          },
          {
            name: 'listScreen',
            label: 'Textos da tela de listagem',
            fields: [
              {
                name: 'titleBoard',
                label: 'Título',
                type: 'LongText',
                rows: 2,
              },
              {
                name: 'subtitleBoard',
                label: 'Subtítulo',
                type: 'LongText',
                rows: 2,
              },
            ],
          },
          {
            name: 'successScreen',
            label: 'Tela de sucesso',
            fields: [
              {
                name: 'imageSuccess',
                label: 'Imagem',
                type: 'ImageFile',
              },
              {
                name: 'altImageSuccess',
                label: 'Texto alternativo da imagem',
                type: 'ShortText',
              },
              {
                name: 'titleSuccess',
                label: 'Título',
                type: 'LongText',
                rows: 2,
              },
              {
                name: 'subtitleSuccess',
                label: 'Subtítulo',
                type: 'LongText',
                rows: 2,
              },
              {
                name: 'stepsSuccess',
                label: 'Ícones e passo a passo',
                type: 'List',
                maxItems: 6,
                itemLabel: 'Passo nº {index}',
                addItemLabel: 'Adicionar passo',
                fields: [
                  {
                    name: 'icon',
                    label: 'Ícone',
                    type: 'ImageFile',
                  },
                  {
                    name: 'altIcon',
                    label: 'Texto alternativo do ícone',
                    type: 'ShortText',
                  },
                  {
                    name: 'text',
                    label: 'Texto de apoio',
                    type: 'Html',
                    wrapWithParagraph: false,
                  },
                ],
              },
            ],
          },
          {
            name: 'integrations',
            label: 'Integrações',
            fields: [
              {
                name: 'travelHubToken',
                type: 'ShortText',
                label: 'Informe o TravelHub Token',
                mask: {
                  mask: /^\S*$/, // remover espaços em branco
                },
              },
              {
                name: 'travelHubHotelCode',
                type: 'ShortText',
                label: 'Informe o TravelHub HotelCode',
                helpText: 'Necessário para integração com PMS',
                isEnabled({ travelHubToken }) {
                  return travelHubToken;
                },
                mask: {
                  mask: /^\S*$/, // remover espaços em branco
                },
              },
            ],
          },
        ],
      },
    ],
  },
};
</script>

<style lang="scss" scoped>
.pre-checkin {
  background-color: $lb-white;
  padding: 56px 0;
  display: flex;

  .validate-integration {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    width: 100%;
    height: 100%;
    z-index: 100;
    background-color: $lb-white;
  }

  > * {
    width: 100%;
    margin: auto;
    padding: 0 16px;
  }

  @include media-breakpoint-up(sm) {
    padding: 96px 0;
  }

  &::v-deep {
    .cta {
      &:not(.vazado) {
        background-color: var(--primary-color);
        box-shadow: 4px 4px 20px rgba($lb-black, 0.25);
        color: $lb-white;
      }

      &.vazado {
        border-color: var(--primary-color);
        color: $lb-black;
      }

      &:disabled {
        pointer-events: none;
        opacity: 0.5;
      }

      @include media-breakpoint-down(sm) {
        width: 100% !important;
        margin-left: 0px !important;
      }
    }

    .title {
      font-weight: bold;
      font-size: 28px;
      letter-spacing: 0.01em;
      color: $lb-black;

      &--small {
        font-size: 20px;
      }

      &--smaller {
        font-size: $size-normal;
      }
    }

    .checkbox {
      input {
        position: absolute;
        opacity: 0;
        height: 0;
        width: 0;

        & + label {
          position: relative;
          padding-left: 20px;
          cursor: pointer;

          &::before {
            content: '';
            position: absolute;
            width: 16px;
            height: 16px;
            left: 0;
            top: 1px;
            border: 1px solid $lb-black;
            border-radius: 1px;
          }

          &::after {
            content: '';
            position: absolute;
            width: 10px;
            height: 10px;
            left: 3px;
            top: 4px;
            border-radius: 1px;
            opacity: 0;
            background-color: var(--primary-color);
            transition: 0.15s;
          }
        }

        &:focus + label::before {
          border-width: 2px;
        }

        &:checked + label::after {
          opacity: 1;
        }
      }
    }
  }
}
</style>
